import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { safeHtml } from "@/lib/safeHtml";
import { useAuth } from "@/contexts/AuthContext";
import { trackEvent } from "@/lib/track";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Loader2,
  Send,
  Clock,
  Download,
  Sparkles,
  Image as ImageIcon,
  Plus,
  ArrowLeftRight,
} from "lucide-react";
import { toast } from "sonner";
import LoadingArt from "@/components/LoadingArt";
import WaitingPresentation from "@/components/WaitingPresentation";
import StyleQuiz from "@/components/StyleQuiz";
import ImageRating from "@/components/ImageRating";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import LightboxGallery from "@/components/LightboxGallery";
import SalesQuoteButton from "@/components/SalesQuoteButton";
import VisionFeedbackDialog from "@/components/VisionFeedbackDialog";
import VisionAiReviewDrawer from "@/components/VisionAiReviewDrawer";
import QualityBadge from "@/components/QualityBadge";
import ShopDeeplinkPanel from "@/components/DashboardSimulate/ShopDeeplinkPanel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function timeRemaining(expIso, expiredLabel = "Expirat") {
  const ms = new Date(expIso) - new Date();
  if (ms <= 0) return expiredLabel;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}

export default function SimulationDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommending, setRecommending] = useState(false);
  const [compareImage, setCompareImage] = useState(null); // for before/after dialog
  // iter 96g — Lightbox state. `lightbox.mode` = "source" | "gen"; `index`
  // is the position in `sim.generated_images` when mode="gen", 0 when
  // mode="source". Kept controlled so the LightboxGallery can drive
  // prev/next by re-emitting `onIndexChange`.
  const [lightbox, setLightbox] = useState({ open: false, mode: "gen", index: 0 });
  const [verifying, setVerifying] = useState(false);
  const [fidelityReport, setFidelityReport] = useState(null);
  // Sprint 3+4 Vision AI — which image is being reviewed in the drawer
  const [reviewingImage, setReviewingImage] = useState(null);

  const runFidelityCheck = async () => {
    if (!sim?.id) return;
    setVerifying(true);
    setFidelityReport(null);
    try {
      const { data } = await api.post(`/simulations/${sim.id}/verify-fidelity`);
      setFidelityReport(data);
      const avg =
        data.results.reduce((s, r) => s + r.fidelity_score, 0) /
        Math.max(1, data.results.length);
      toast.success(t("detail.toast_fidelity_avg", { avg: avg.toFixed(1) }));
    } catch (e) {
      toast.error(t("detail.toast_fidelity_failed"));
    } finally {
      setVerifying(false);
    }
  };
  const [rec, setRec] = useState({ notes: "" });
  const [avgDuration, setAvgDuration] = useState(60);
  const [moreLoading, setMoreLoading] = useState(false);

  const fetchSim = useCallback(async ({ isInitial = false } = {}) => {
    try {
      const { data } = await api.get(`/simulations/${id}`);
      setSim(data);
      setLoading(false);
    } catch (e) {
      const status = e.response?.status;
      // Only redirect on explicit 404/403 during the INITIAL load.
      // Transient errors (502 from ingress during long generations, network blips, etc.)
      // must NOT kick the user out of the waiting screen — the backend keeps generating.
      if (isInitial && (status === 404 || status === 403)) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("detail.toast_sim_not_found"));
        navigate("/dashboard");
        return;
      }
      // Swallow transient polling errors; next tick will retry.
      // eslint-disable-next-line no-console
      console.warn("Simulation poll transient error:", status || e.message);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    fetchSim({ isInitial: true });
    // Faster polling so progressive updates feel live
    const t = setInterval(() => {
      fetchSim();
    }, 2500);
    return () => clearInterval(t);
  }, [fetchSim]);

  // Context-aware ETA: refresh whenever we learn the sim's type/mode/count.
  // Backend filters by sim_type + render_mode + variations so a RENOVATION
  // EXTERIOR estimate doesn't get diluted by fast INTERIOR STANDARD runs.
  useEffect(() => {
    if (!sim || !sim.sim_type) return;
    const params = new URLSearchParams({ sim_type: sim.sim_type });
    if (sim.render_mode) params.set("render_mode", sim.render_mode);
    if (sim.requested_count) params.set("variations", String(sim.requested_count));
    api
      .get(`/simulations/avg-duration?${params.toString()}`)
      .then((r) => setAvgDuration(r.data.avg_seconds))
      .catch(() => {});
  }, [sim?.sim_type, sim?.render_mode, sim?.requested_count]);

  const generateMore = async (count = 2) => {
    setMoreLoading(true);
    try {
      const fd = new FormData();
      fd.append("count", String(count));
      await api.post(`/simulations/${id}/more`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("detail.toast_more_added", { count }));
      fetchSim();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("detail.toast_generic_error"));
    } finally {
      setMoreLoading(false);
    }
  };

  const sendRecommendation = async () => {
    setRecommending(true);
    try {
      await api.post("/recommendations", { simulation_id: id, notes: rec.notes });
      trackEvent("reco.sent", {
        sim_id: id,
        has_notes: !!(rec.notes && rec.notes.trim()),
      });
      toast.success(t("detail.toast_rec_sent"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("detail.toast_rec_failed"));
    } finally {
      setRecommending(false);
    }
  };

  const downloadAll = async () => {
    if (!sim?.generated_images?.length) return;
    toast.info(t("detail.toast_preparing_zip"));
    try {
      const resp = await api.get(`/simulations/${sim.id}/zip`, {
        responseType: "blob",
      });
      const blob = resp.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FDP_${sim.sim_type}_${sim.style}_${sim.id.slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("detail.toast_zip_done"));
    } catch (e) {
      toast.error(t("detail.toast_zip_failed"));
    }
  };

  const downloadOne = async (img, idx) => {
    try {
      const resp = await fetch(fileUrl(img.url));
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FDP_${sim.sim_type}_${sim.style}_var${idx + 1}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("detail.toast_var_downloaded", { n: idx + 1 }));
    } catch (e) {
      toast.error(t("detail.toast_download_failed"));
    }
  };

  if (loading || !sim) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="text-orange-500 animate-spin" size={40} />
      </div>
    );
  }

  const isProcessing = sim.status === "pending" || sim.status === "generating";
  const isFailed = sim.status === "failed";
  const remaining = timeRemaining(sim.expires_at, t("detail.expired"));
  const isPartnerOrSales = user && (user.role === "partner" || user.role === "sales");
  const isAdminOrSales = user && (user.role === "admin" || user.role === "sales");
  const canReviewVision = user && ["partner", "sales", "admin"].includes(user.role);

  const toggleFeature = async (img, idx) => {
    const newState = !img.featured;
    try {
      await api.post(`/admin/gallery/${img.id}/feature`, { featured: newState });
      toast.success(newState ? t("detail.toast_gallery_added") : t("detail.toast_gallery_removed"));
      // optimistic update
      setSim((s) => {
        if (!s) return s;
        const next = { ...s, generated_images: [...(s.generated_images || [])] };
        next.generated_images[idx] = { ...next.generated_images[idx], featured: newState };
        return next;
      });
    } catch (e) {
      toast.error(t("detail.toast_gallery_error"));
    }
  };

  const togglePin = async (img, idx) => {
    const newState = !img.pinned;
    try {
      await api.post(`/admin/gallery/${img.id}/pin`, { pinned: newState });
      toast.success(newState ? t("detail.toast_pinned") : t("detail.toast_unpinned"));
      setSim((s) => {
        if (!s) return s;
        const next = { ...s, generated_images: [...(s.generated_images || [])] };
        next.generated_images[idx] = { ...next.generated_images[idx], pinned: newState };
        return next;
      });
    } catch (e) {
      toast.error(t("detail.toast_pin_error"));
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="simulation-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-20">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-orange-400 transition mb-6"
          data-testid="sim-back-link"
        >
          <ArrowLeft size={14} /> {t("detail.back_to_dashboard")}
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-orange-500 mb-3">
              <span data-testid="sim-type-badge">{sim.sim_type}</span>
              <span className="text-zinc-700">·</span>
              <span data-testid="sim-style-badge">{sim.style.replace("_", " ")}</span>
            </div>
            <h1 className="font-outfit font-black text-white text-4xl md:text-5xl tracking-tighter leading-tight">
              {t("detail.title_short", { id: sim.id.slice(0, 8) })}
            </h1>
            {sim.client_name && (
              <p className="text-zinc-400 mt-2">
                {t("detail.client_label")} <span className="text-white">{sim.client_name}</span> · {sim.client_email}
              </p>
            )}
          </div>

          <div
            className="border border-orange-500/30 bg-orange-500/5 rounded-md px-4 py-2 flex items-center gap-2"
            data-testid="sim-expires-badge"
          >
            <Clock size={14} className="text-orange-500" strokeWidth={1.5} />
            <span className="text-sm">
              <span className="text-zinc-500">{t("detail.expires_in_label")}</span>{" "}
              <span className="text-orange-400 font-medium">{remaining}</span>
            </span>
          </div>
        </div>

        {/* Source image */}
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-4">
              <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3 px-1">
                {t("detail.source_image")}
              </div>
              <button
                type="button"
                onClick={() => setLightbox({ open: true, mode: "source", index: 0 })}
                className="w-full block cursor-zoom-in"
                data-testid="source-image-open"
                aria-label={t("detail.source_image") + " — click pentru mărire"}
              >
                <img
                  src={fileUrl(sim.source_image_url)}
                  alt="sursa"
                  className="w-full rounded-md transition group-hover:opacity-95"
                  data-testid="source-image"
                />
              </button>
            </div>
          </div>

          <div className="lg:col-span-8">
            {isProcessing && (
              <div data-testid="processing-state" className="space-y-4">
                {/* Progressive: show partial images as they come in */}
                {sim.generated_images && sim.generated_images.length > 0 && (
                  <div className="border border-orange-500/20 bg-orange-500/5 rounded-md p-4">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-400 mb-3">
                      <Loader2 size={12} className="animate-spin" />
                      {t("detail.image_generated_progress", { current: sim.generated_images.length, total: sim.requested_count || 3 })}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {sim.generated_images.map((img, idx) => (
                        <div
                          key={img.id}
                          className="relative aspect-[4/3] overflow-hidden rounded-md border border-white/10"
                          data-testid={`partial-image-${idx}`}
                        >
                          <img
                            src={fileUrl(img.url)}
                            alt={`var ${idx + 1}`}
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-black/60 backdrop-blur text-[9px] uppercase tracking-[0.2em] text-orange-400 rounded">
                            {idx + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <WaitingPresentation
                  simType={sim.sim_type}
                  style={sim.style}
                  mockTimeSec={avgDuration}
                  simulationId={sim.id}
                  generatedCount={sim.generated_images?.length || 0}
                  requestedCount={sim.requested_count || 3}
                />
              </div>
            )}

            {isFailed && (
              <div
                className="h-full min-h-[400px] border border-red-500/20 rounded-lg bg-red-500/5 p-8 flex flex-col items-center justify-center text-center"
                data-testid="failed-state"
              >
                {sim.error === "AI_BUDGET_EXCEEDED" ? (
                  <>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3">
                      {t("detail.failed_budget_ai_tag")}
                    </div>
                    <h3 className="font-outfit font-bold text-white text-2xl tracking-tight mb-3">
                      {t("detail.failed_budget_ai_title")}
                    </h3>
                    <p
                      className="text-zinc-400 max-w-md leading-relaxed"
                      dangerouslySetInnerHTML={safeHtml(t("detail.failed_budget_ai_body"))}
                    />
                    <Link to="/dashboard">
                      <Button className="mt-6 bg-orange-500 hover:bg-orange-600">
                        {t("detail.failed_back_btn")}
                      </Button>
                    </Link>
                  </>
                ) : sim.error === "FAL_BUDGET_EXCEEDED" ? (
                  <>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3">
                      {t("detail.failed_budget_fal_tag")}
                    </div>
                    <h3 className="font-outfit font-bold text-white text-2xl tracking-tight mb-3">
                      {t("detail.failed_budget_fal_title")}
                    </h3>
                    <p
                      className="text-zinc-400 max-w-md leading-relaxed"
                      dangerouslySetInnerHTML={safeHtml(
                        t("detail.failed_budget_fal_body").replace(
                          "<a>",
                          '<a href="https://fal.ai/dashboard/billing" target="_blank" rel="noopener noreferrer" class="text-orange-400 hover:text-orange-300 underline">'
                        ),
                      )}
                    />
                    <Link to="/dashboard">
                      <Button className="mt-6 bg-orange-500 hover:bg-orange-600">
                        {t("detail.failed_back_btn")}
                      </Button>
                    </Link>
                  </>
                ) : sim.error === "AI_SAFETY_FILTER" ? (
                  <>
                    <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3">
                      {t("detail.failed_safety_tag")}
                    </div>
                    <h3 className="font-outfit font-bold text-white text-2xl tracking-tight mb-3">
                      {t("detail.failed_safety_title")}
                    </h3>
                    <p
                      className="text-zinc-400 max-w-md leading-relaxed"
                      dangerouslySetInnerHTML={safeHtml(t("detail.failed_safety_body"))}
                    />
                    <Link to="/dashboard">
                      <Button className="mt-6 bg-orange-500 hover:bg-orange-600">
                        {t("detail.failed_try_other_image")}
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <h3 className="font-outfit font-bold text-white text-2xl tracking-tight">
                      {t("detail.failed_generic_title")}
                    </h3>
                    <p className="text-zinc-400 mt-2 max-w-md">
                      {sim.error || t("detail.failed_generic_body")}
                    </p>
                    <p className="text-zinc-500 text-sm mt-4 max-w-md">
                      {t("detail.failed_generic_hint")}
                    </p>
                    <Link to="/dashboard">
                      <Button className="mt-6 bg-orange-500 hover:bg-orange-600">
                        {t("detail.failed_try_again_btn")}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            )}

            {sim.status === "completed" && (
              <>
                {/* iter96 · AI disclaimer required by legal team on generated-images page */}
                <div
                  className="mb-5 p-4 rounded-lg border border-amber-500/25 bg-amber-500/5 flex gap-3"
                  data-testid="ai-disclaimer-generated"
                >
                  <div className="text-amber-400 text-lg leading-none pt-0.5" aria-hidden>ⓘ</div>
                  <p className="text-xs md:text-sm text-amber-100/90 leading-relaxed">
                    <span className="font-semibold text-amber-300">Imagini generate cu AI</span>
                    {" "}— pot exista diferențe/distorsiuni față de situația reală și nu creează
                    obligații contractuale. AI-ul inspiră și susține procesul de design, dar
                    nu înlocuiește consultanța, experiența și atenția la detalii a
                    specialiștilor FDP.
                  </p>
                </div>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">
                    {t("detail.variations_count_completed", { count: sim.generated_images.length })}
                    {sim.duration_seconds && (
                      <span className="ml-2 text-zinc-600">
                        · {Math.round(sim.duration_seconds)}s
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {sim.generated_images.length < 10 && sim.user_id === user?.id && (
                      <Button
                        onClick={() => generateMore(2)}
                        disabled={moreLoading}
                        variant="outline"
                        size="sm"
                        className="bg-zinc-900/50 border-orange-500/30 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300"
                        data-testid="more-variations-btn"
                      >
                        {moreLoading ? (
                          <Loader2 size={14} className="animate-spin mr-2" />
                        ) : (
                          <Plus size={14} className="mr-2" />
                        )}
                        {t("detail.more_variations_btn")}
                      </Button>
                    )}
                    <Button
                      onClick={runFidelityCheck}
                      disabled={verifying}
                      variant="outline"
                      size="sm"
                      className="bg-zinc-900/50 border-orange-500/30 text-orange-400 hover:bg-orange-500/10 hover:border-orange-500/50"
                      data-testid="verify-fidelity-btn"
                    >
                      {verifying ? (
                        <Loader2 size={14} className="mr-2 animate-spin" />
                      ) : (
                        <Sparkles size={14} className="mr-2" />
                      )}
                      {t("detail.verify_fidelity_btn")}
                    </Button>
                    <Button
                      onClick={downloadAll}
                      variant="outline"
                      size="sm"
                      className="bg-zinc-900/50 border-white/10 text-white hover:bg-orange-500/10 hover:border-orange-500/30 hover:text-orange-400"
                      data-testid="download-all-btn"
                    >
                      <Download size={14} className="mr-2" /> {t("detail.download_all_btn")}
                    </Button>
                  </div>
                </div>
                {fidelityReport && (
                  <div className="mb-4 rounded-lg border border-orange-500/20 bg-orange-500/5 p-4" data-testid="fidelity-report">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-orange-400 font-bold">
                        {t("detail.fidelity_report_title")}
                      </div>
                      <button
                        onClick={() => setFidelityReport(null)}
                        className="text-zinc-500 hover:text-white text-xs"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="space-y-2">
                      {fidelityReport.results.map((r) => (
                        <div
                          key={r.image_id}
                          className="rounded border border-white/10 bg-zinc-900/40 p-3"
                          data-testid={`fidelity-${r.variation_index}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-xs font-bold text-white">
                              {t("detail.fidelity_variation", { n: r.variation_index })}
                            </div>
                            <div className="flex items-center gap-2 text-[11px]">
                              <span className="text-zinc-400">{t("detail.fidelity_label")}</span>
                              <span
                                className={`font-bold ${
                                  r.fidelity_score >= 4
                                    ? "text-emerald-400"
                                    : r.fidelity_score >= 3
                                    ? "text-amber-400"
                                    : "text-red-400"
                                }`}
                              >
                                {r.fidelity_score}/5
                              </span>
                              <span className="text-zinc-600">·</span>
                              <span className="text-zinc-400">{t("detail.decor_label")}</span>
                              <span className="font-bold text-white">
                                {r.decoration_quality}/5
                              </span>
                            </div>
                          </div>
                          {r.verdict && (
                            <div className="text-xs text-zinc-300 italic mb-1">
                              "{r.verdict}"
                            </div>
                          )}
                          {r.preserved_correctly?.length > 0 && (
                            <div className="text-[10px] text-emerald-300 mt-1">
                              ✓ {t("detail.preserved_label")} {r.preserved_correctly.join(" · ")}
                            </div>
                          )}
                          {r.issues?.length > 0 && (
                            <div className="text-[10px] text-amber-300 mt-0.5">
                              ⚠ {t("detail.issues_label")} {r.issues.join(" · ")}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="generated-grid">
                  {sim.generated_images.map((img, idx) => (
                    <div
                      key={img.id}
                      className="group relative overflow-hidden rounded-md border border-white/10 hover:border-orange-500 transition-all"
                      data-testid={`generated-image-${idx}`}
                    >
                      <div className="relative aspect-[4/3]">
                        <img
                          src={fileUrl(img.url)}
                          alt={`var ${idx + 1}`}
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 backdrop-blur text-[10px] uppercase tracking-[0.2em] text-orange-400 rounded">
                          {t("detail.var_short", { n: idx + 1 })}
                        </div>
                        {img.preservation_retried && isAdminOrSales && (
                          <div
                            className="absolute top-2 right-2 px-2 py-0.5 bg-emerald-500/20 border border-emerald-500/40 backdrop-blur text-[10px] uppercase tracking-[0.18em] text-emerald-300 rounded flex items-center gap-1"
                            title={t("detail.auto_correct_tooltip", {
                              n: img.preservation_retry_count || 1,
                              score: img.preservation_score != null ? t("detail.auto_correct_score", { score: Number(img.preservation_score).toFixed(2) }) : "",
                            })}
                            data-testid={`auto-correct-badge-${idx}`}
                          >
                            <Sparkles size={10} /> {t("detail.auto_corrected", { n: img.preservation_retry_count || 1 })}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            downloadOne(img, idx);
                          }}
                          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/90 hover:bg-orange-500 text-white text-xs font-medium rounded backdrop-blur transition opacity-0 group-hover:opacity-100"
                          data-testid={`download-${idx}`}
                        >
                          <Download size={12} /> {t("detail.btn_download")}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setCompareImage({ img, idx });
                          }}
                          className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/70 hover:bg-black text-white text-xs font-medium rounded backdrop-blur transition opacity-0 group-hover:opacity-100 border border-white/20"
                          data-testid={`compare-${idx}`}
                        >
                          <ArrowLeftRight size={12} /> {t("detail.btn_before_after")}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setLightbox({ open: true, mode: "gen", index: idx });
                          }}
                          className="absolute inset-0 cursor-zoom-in"
                          data-testid={`open-${idx}`}
                          aria-label={t("detail.btn_open_in_tab")}
                        />
                      </div>
                      {Array.isArray(img.product_codes_used) && img.product_codes_used.length > 0 && (
                        <div
                          className="bg-zinc-950/80 border-t border-white/5 px-3 py-2 text-xs text-zinc-300"
                          data-testid={`product-codes-${idx}`}
                        >
                          <span className="text-zinc-500">{t("detail.we_used")} </span>
                          {img.product_codes_used.map((code, i) => {
                            const isLast = i === img.product_codes_used.length - 1;
                            const isFirst = i === 0;
                            // Backend enriches with parallel array of {code,url}
                            const linkObj = (img.product_links_used || []).find((l) => l.code === code);
                            const url = linkObj?.url;
                            return (
                              <React.Fragment key={code}>
                                {!isFirst && (isLast ? <span className="text-zinc-500"> {t("common.and")} </span> : <span className="text-zinc-500">, </span>)}
                                {url ? (
                                  <a
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="font-mono text-orange-400 font-medium uppercase underline decoration-orange-500/30 hover:decoration-orange-400 hover:text-orange-300 transition"
                                    data-testid={`product-code-link-${idx}-${code}`}
                                    title={`Deschide pagina categoriei pe fabricadeprofile.ro și caută ${code}`}
                                  >
                                    cp {code}
                                  </a>
                                ) : (
                                  <span className="font-mono text-orange-400 font-medium uppercase">cp {code}</span>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                      {/* Sprint 3+4 Vision AI — review trigger visible to
                          partner/sales/admin (canReviewVision). Sits outside
                          the admin-only rating panel so partners get their
                          own review entry point. */}
                      {canReviewVision && !isAdminOrSales && (
                        <div className="bg-zinc-950/80 border-t border-white/5 px-3 py-2 flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => setReviewingImage(img)}
                            data-testid={`vision-review-btn-partner-${idx}`}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold transition bg-orange-500/15 text-orange-300 border border-orange-500/30 hover:bg-orange-500/25"
                            title={t("vision_ai.review_btn_tooltip")}
                          >
                            <Sparkles size={10} strokeWidth={1.5} />
                            {t("vision_ai.review_btn_label")}
                          </button>
                        </div>
                      )}
                      {/* Rating bar (below image) — admin/sales only.
                          Removed from regular users to prevent rating gaming
                          (a malicious user could 5★ a bad render and 1★ a good
                          one). Curation is admin-only by design. */}
                      {isAdminOrSales && (
                      <div className="bg-zinc-950/80 border-t border-white/5 px-3 py-2 flex items-center justify-between flex-wrap gap-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                          {t("detail.mark_internal_admin")}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <ImageRating
                            simulationId={sim.id}
                            imageId={img.id}
                            initialRating={img.rating || 0}
                          />
                          <VisionFeedbackDialog sim={sim} image={img} />
                          {canReviewVision && (
                            <button
                              type="button"
                              onClick={() => setReviewingImage(img)}
                              data-testid={`vision-review-btn-${idx}`}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold transition bg-orange-500/15 text-orange-300 border border-orange-500/30 hover:bg-orange-500/25"
                              title={t("vision_ai.review_btn_tooltip")}
                            >
                              <Sparkles size={10} strokeWidth={1.5} />
                              {t("vision_ai.review_btn_label")}
                            </button>
                          )}
                          <QualityBadge
                            check={img.quality_check}
                            slugNames={Object.fromEntries(
                              (sim.selected_categories_full || []).map((c) => [c.slug, c.name]),
                            )}
                          />
                          <button
                            type="button"
                            onClick={() => toggleFeature(img, idx)}
                            className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold transition ${
                              img.featured
                                ? "bg-emerald-500 text-white hover:bg-emerald-600"
                                : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-white/10"
                            }`}
                            data-testid={`feature-toggle-${idx}`}
                          >
                            {img.featured ? t("detail.feature_in_gallery") : t("detail.feature_add_gallery")}
                          </button>
                          {img.featured && (
                            <button
                              type="button"
                              onClick={() => togglePin(img, idx)}
                              className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold transition ${
                                img.pinned
                                  ? "bg-orange-500 text-white hover:bg-orange-600"
                                  : "bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-white/10"
                              }`}
                              data-testid={`pin-toggle-${idx}`}
                              title={t("detail.pin_tooltip")}
                            >
                              {img.pinned ? t("detail.pin_on_homepage") : t("detail.pin_add_homepage")}
                            </button>
                          )}
                        </div>
                      </div>
                      )}
                    </div>
                  ))}
                </div>
                {/* iter 86 — Order-from-shop CTA. Renders only when the
                    backend was able to aggregate one or more FDP codes. */}
                <ShopDeeplinkPanel shopLinks={sim.shop_links} />
              </>
            )}
          </div>
        </div>

        {/* Sales CTA — visible to client roles only (simple_user, partner).
            Hidden for admin/sales staff. Only shows on completed sims. */}
        {sim.status === "completed" && !isAdminOrSales && (
          <div
            className="mt-12 border border-orange-500/30 rounded-lg bg-gradient-to-br from-orange-500/10 to-amber-500/5 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            data-testid="sales-cta-block"
          >
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-orange-400 mb-2">
                {t("detail.next_step")}
              </div>
              <h3 className="font-outfit font-bold text-white text-2xl tracking-tight">
                {t("detail.want_to_order")}
              </h3>
              <p className="text-zinc-400 mt-2 max-w-xl text-sm">
                {t("detail.cta_body")}
              </p>
            </div>
            <SalesQuoteButton
              simulationId={sim.id}
              testId="sales-quote-btn-simulation"
            />
          </div>
        )}

        {/* Recommendation block (partner/sales internal flow) */}
        {sim.status === "completed" && isPartnerOrSales && (
          <div
            className="mt-12 border border-orange-500/20 rounded-lg bg-orange-500/5 p-6 md:p-8"
            data-testid="recommendation-block"
          >
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-2">
                  {t("detail.rec_action")}
                </div>
                <h3 className="font-outfit font-bold text-white text-2xl tracking-tight">
                  {t("detail.rec_send_title")}
                </h3>
                <p className="text-zinc-400 mt-2 max-w-xl">
                  {t("detail.rec_send_body")}
                </p>
              </div>
              <div className="flex flex-col gap-2 md:min-w-[400px]">
                <textarea
                  placeholder={t("detail.rec_notes_placeholder")}
                  rows={3}
                  value={rec.notes}
                  onChange={(e) => setRec({ notes: e.target.value })}
                  className="w-full bg-zinc-950/50 border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-orange-500"
                  data-testid="rec-notes-textarea"
                />
                <Button
                  onClick={sendRecommendation}
                  disabled={recommending}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="send-recommendation-btn"
                >
                  {recommending ? (
                    <Loader2 size={16} className="animate-spin mr-2" />
                  ) : (
                    <Send size={16} className="mr-2" />
                  )}
                  {t("detail.rec_send_btn")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      <Dialog open={!!compareImage} onOpenChange={(o) => !o && setCompareImage(null)}>
        <DialogContent className="max-w-5xl bg-zinc-950 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-outfit font-bold">
              {t("detail.compare_dialog_title", { n: (compareImage?.idx ?? 0) + 1 })}
            </DialogTitle>
          </DialogHeader>
          {compareImage && sim?.source_image_url && (
            <>
              <BeforeAfterSlider
                beforeSrc={fileUrl(sim.source_image_url)}
                afterSrc={fileUrl(compareImage.img.url)}
              />
              <p className="text-xs text-zinc-500 text-center mt-2">
                {t("detail.compare_dialog_hint")}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* iter 96g — In-page lightbox for source + generated variations.
          Client feedback: variations must not open in a new tab; source
          must be zoomable. Keyboard: ← → ESC. */}
      {lightbox.open && sim && (() => {
        const gens = sim.generated_images || [];
        const items = lightbox.mode === "source"
          ? [{
              src: fileUrl(sim.source_image_url),
              alt: "Imagine sursă",
              label: t("detail.source_image", { defaultValue: "Imagine sursă" }),
            }]
          : gens.map((img, i) => ({
              src: fileUrl(img.url),
              downloadUrl: fileUrl(img.url),
              alt: `Varianta ${i + 1}`,
              label: `Varianta ${i + 1} / ${gens.length}`,
            }));
        const idx = Math.max(0, Math.min(lightbox.index, items.length - 1));
        const compareCb = lightbox.mode === "gen" && gens[idx]
          ? () => {
              setCompareImage({ img: gens[idx], idx });
              setLightbox({ open: false, mode: "gen", index: 0 });
            }
          : null;
        return (
          <LightboxGallery
            images={items}
            index={idx}
            onIndexChange={(nextIdx) =>
              setLightbox((prev) => ({ ...prev, index: nextIdx }))
            }
            onClose={() => setLightbox({ open: false, mode: "gen", index: 0 })}
            onCompare={compareCb}
          />
        );
      })()}

      {/* Sprint 3 + 4 Vision AI — unified review drawer */}
      {reviewingImage && (
        <VisionAiReviewDrawer
          sim={sim}
          image={reviewingImage}
          open={!!reviewingImage}
          onOpenChange={(o) => !o && setReviewingImage(null)}
        />
      )}
    </div>
  );
}
